import { useCallback, useEffect, useState } from 'react'
import { App } from 'antd'
import { useNavigate } from 'react-router-dom'
import type { SurveyResponsesResponse } from '@bandos/shared'
import { SurveyService } from '../services'
import { supabase } from '../../../utils/supabase'

export interface UseSurveyResponsesDataResult {
  data: SurveyResponsesResponse | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Loads the authenticated survey responses payload, manages loading/error
 * state, and subscribes to the per-survey realtime channel so the page
 * refreshes when new responses arrive or synthesis completes.
 */
export function useSurveyResponsesData(nodeId: string | undefined): UseSurveyResponsesDataResult {
  const { notification } = App.useApp()
  const navigate = useNavigate()
  const [data, setData] = useState<SurveyResponsesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    if (!nodeId) return
    const result = await SurveyService.getResponses(nodeId)
    if (result.data) setData(result.data)
  }, [nodeId])

  useEffect(() => {
    if (!nodeId) return
    void (async () => {
      setLoading(true)
      const result = await SurveyService.getResponses(nodeId)
      setLoading(false)
      if (result.error || !result.data) {
        const status = (result.error as { status?: number } | null)?.status
        if (status === 401 || status === 403) {
          notification.error({ title: 'Access denied' })
          void navigate('/')
          return
        }
        setError('Survey not found or you do not have access.')
        return
      }
      setData(result.data)
    })()
  }, [nodeId, navigate, notification])

  useEffect(() => {
    if (!nodeId) return undefined
    const refresh = () => void refetch()
    const channel = supabase
      .channel(`survey:${nodeId}`)
      .on('broadcast', { event: 'response_submitted' }, refresh)
      .on('broadcast', { event: 'summary_generated' }, refresh)
      .subscribe()
    return () => {
      void supabase.removeChannel(channel)
    }
  }, [nodeId, refetch])

  return { data, loading, error, refetch }
}
